import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Database, Loader2, Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import {
  createConnection,
  deleteConnection,
  listConnections,
  testConnection,
  updateConnection,
} from "@/api/connections";
import type { OneCBaseList } from "@/api/onec_bases";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import { ssoConnectionProblem, useOneCBases } from "@/lib/use-onec-bases";
import { SsoConnectionDialog } from "@/pages/profile/SsoConnectionDialog";
import type { OneCConnectionView } from "@/types/api";

export function ConnectionsManager(): React.ReactElement {
  const queryClient = useQueryClient();
  // SSO-сессия: в 1С портал ходит по JWT в базу подключения, а она должна быть
  // в справочнике баз. Поэтому новое подключение — выбор базы из справочника,
  // без логина и пароля; правки логина/пароля в SSO нет.
  const { user } = useAuth();
  const sso = user?.auth_mode === "sso";
  const bases = useOneCBases();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OneCConnectionView | null>(null);

  const query = useQuery({
    queryKey: ["connections"],
    queryFn: listConnections,
  });

  const handleCreated = () => {
    setFormOpen(false);
    setEditing(null);
    queryClient.invalidateQueries({ queryKey: ["connections"] });
  };

  const deleteMutation = useMutation({
    mutationFn: deleteConnection,
    onSuccess: () => {
      toast.success("Подключение удалено");
      queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });

  const testMutation = useMutation({
    mutationFn: testConnection,
    onSuccess: (result) => {
      if (result.ok) toast.success("Подключение работает");
      else toast.error(`Ошибка: ${result.detail}`);
    },
    // В SSO проверка идёт по JWT и может вернуть 403 с причиной (нет базы в
    // справочнике, нет пользователя 1С) — показываем её, а не общую фразу.
    onError: (e: unknown) => toast.error(extractErrorMessage(e, "Не удалось проверить")),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Database className="size-5" />
            Подключения к 1С
          </span>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="size-4" />
            Добавить
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sso && (
          <p className="text-sm text-muted-foreground">
            Вы вошли через SSO: в 1С портал входит токеном SSO от имени пользователя 1С, заданного
            в вашей учётной записи SSO. Логин и пароль 1С не нужны — подключение заводится
            выбором базы из справочника.
          </p>
        )}
        {bases.error && (
          <p className="text-sm text-destructive">
            Справочник баз 1С не загрузился: {extractErrorMessage(bases.error, "нет ответа")}
          </p>
        )}
        {query.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить подключения: {extractErrorMessage(query.error, "нет ответа")}
          </p>
        )}
        {query.isLoading && (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        )}
        {query.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Нет подключений. Добавьте первое подключение к 1С.
          </p>
        )}
        {query.data?.map((conn) => (
          <div
            key={conn.id}
            className="flex items-center justify-between gap-3 rounded-md border p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{conn.name}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {conn.config_type.toUpperCase()}
                </span>
                {conn.is_default && (
                  <span className="text-xs text-green-700">по умолчанию</span>
                )}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {conn.odata_url}
              </div>
              <ConnectionNote conn={conn} sso={sso} bases={bases.data} />
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={testMutation.isPending}
                onClick={() => testMutation.mutate(conn.id)}
              >
                {testMutation.isPending &&
                testMutation.variables === conn.id ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  "Проверить"
                )}
              </Button>
              {/* Правка — логин/пароль/адрес, это парольное подключение. В SSO
                  подключение меняют пересозданием из справочника. */}
              {!sso && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(conn);
                    setFormOpen(true);
                  }}
                >
                  Изменить
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (
                    confirm(
                      `Удалить подключение «${conn.name}»? Все маппинги и созданные документы этого подключения будут удалены.`,
                    )
                  )
                    deleteMutation.mutate(conn.id);
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>

      {sso ? (
        <SsoConnectionDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={handleCreated}
        />
      ) : (
        <ConnectionFormDialog
          open={formOpen}
          editing={editing}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSaved={handleCreated}
        />
      )}
    </Card>
  );
}

/** Почему подключение не заработает в текущей сессии: в SSO — нет базы в
 *  справочнике или пользователя 1С для неё; по паролю — у подключения из
 *  справочника нет логина/пароля (бэкенд ответит 403 `onec_password_not_set`). */
function ConnectionNote({
  conn,
  sso,
  bases,
}: {
  conn: OneCConnectionView;
  sso: boolean;
  bases: OneCBaseList | undefined;
}): React.ReactElement | null {
  let problem: string | null = null;
  if (sso) {
    problem = bases ? ssoConnectionProblem(conn, bases) : null;
  } else if (!conn.has_password) {
    problem =
      "Подключение без логина и пароля 1С (заведено из справочника для SSO) — при входе по " +
      "паролю не работает. Задайте логин и пароль через «Изменить» или войдите через SSO.";
  }
  if (problem === null) return null;
  return (
    <div
      role="alert"
      className="mt-1 flex gap-1.5 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900"
    >
      <AlertTriangle className="mt-0.5 size-3 shrink-0" />
      <span>{problem}</span>
    </div>
  );
}

function ConnectionFormDialog({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: OneCConnectionView | null;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const [name, setName] = useState("");
  const [configType, setConfigType] = useState("bp");
  const [url, setUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset on open.
  React.useEffect(() => {
    if (open) {
      if (editing) {
        setName(editing.name);
        setConfigType(editing.config_type);
        setUrl(editing.odata_url);
        setUsername(editing.odata_username ?? "");
        setPassword("");
      } else {
        setName("");
        setConfigType("bp");
        setUrl("");
        setUsername("");
        setPassword("");
      }
    }
  }, [open, editing]);

  // Новое подключение — логин и пароль обязательны. У подключения из справочника
  // (логина нет) их задают только вместе — или не трогают вовсе (переименование).
  const fromDirectoryNoLogin = editing !== null && editing.odata_username === null;
  const credentialsInvalid = fromDirectoryNoLogin
    ? (username.trim() === "") !== (password.trim() === "")
    : !username.trim() || (editing === null && !password.trim());

  const handleSubmit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await updateConnection(editing.id, {
          name: name || undefined,
          config_type: configType || undefined,
          odata_url: url || undefined,
          odata_username: username || undefined,
          odata_password: password || undefined,
        });
        toast.success("Подключение обновлено");
      } else {
        await createConnection({
          name,
          config_type: configType,
          odata_url: url,
          odata_username: username,
          odata_password: password,
        });
        toast.success("Подключение создано");
      }
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Ошибка"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Редактировать подключение" : "Новое подключение к 1С"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Название</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="БП ИП Еграшкин"
            />
          </div>
          <div className="space-y-2">
            <Label>Тип конфигурации</Label>
            <select
              className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm"
              value={configType}
              onChange={(e) => setConfigType(e.target.value)}
            >
              <option value="bp">Бухгалтерия предприятия (БП)</option>
              <option value="unf">Управление нашей фирмой (УНФ)</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>URL OData</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.currentTarget.value)}
              placeholder="https://1c.example.ru/bp_3/odata/standard.odata"
            />
          </div>
          {fromDirectoryNoLogin && (
            <p className="text-xs text-muted-foreground">
              Подключение заведено из справочника баз без логина 1С (для входа через SSO). Логин и
              пароль 1С задаются только вместе — или оставьте оба пустыми.
            </p>
          )}
          <div className="space-y-2">
            <Label>Логин</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Пароль</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              placeholder={editing ? "(оставьте пустым чтобы не менять)" : ""}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              busy ||
              !name.trim() ||
              !url.trim() ||
              credentialsInvalid
            }
          >
            {busy ? "Сохранение…" : editing ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
